import React from 'react';
import Icon from '../../../components/AppIcon';
import GlowRing from '../../../components/ui/GlowRing';

const SleepGoalProgress = ({ currentSleep, sleepGoal, weeklyAverage }) => {
  const calculateProgress = (current, goal) => {
    const currentHours = parseFloat(current?.replace('h', ''));
    const goalHours = parseFloat(goal?.replace('h', ''));
    return Math.min((currentHours / goalHours) * 100, 100);
  };

  const progress = calculateProgress(currentSleep, sleepGoal);
  const weeklyProgress = calculateProgress(weeklyAverage, sleepGoal);

  const getRingColor = (p) => {
    if (p >= 90) return 'lime';
    if (p >= 70) return 'cyan';
    return 'orange';
  };

  const getStatusMessage = () => {
    if (progress >= 100) return "Perfect! You've reached your sleep goal.";
    if (progress >= 90) return "Great job! Almost at your goal.";
    if (progress >= 70) return "Good progress towards your sleep goal.";
    return "You need more sleep to reach your goal.";
  };

  const getRecommendation = () => {
    const currentHours = parseFloat(currentSleep?.replace('h', ''));
    const goalHours = parseFloat(sleepGoal?.replace('h', ''));
    const deficit = goalHours - currentHours;
    if (deficit <= 0) return "Maintain your excellent sleep routine!";
    if (deficit <= 1) return `Try to sleep ${Math.round(deficit * 60)} minutes earlier tonight.`;
    return `Consider going to bed ${Math.round(deficit)} hours earlier to meet your goal.`;
  };

  const getStatusColor = (p) => {
    if (p >= 90) return '#AAFF00';
    if (p >= 70) return '#00D4FF';
    return '#FF6B00';
  };

  return (
    <div className="glass-card p-6 shadow-neon-cyan">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg" style={{ background: 'rgba(0,212,255,0.1)' }}>
            <Icon name="Target" size={20} style={{ color: '#00D4FF' }} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground" style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Sleep Goal</h3>
            <p className="text-sm text-muted-foreground">Daily target: {sleepGoal}</p>
          </div>
        </div>
      </div>

      {/* Progress Rings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Today's Progress */}
        <div className="text-center flex flex-col items-center gap-3">
          <GlowRing
            value={Math.round(progress)}
            max={100}
            color={getRingColor(progress)}
            size={140}
            unit="complete"
            displayValue={`${Math.round(progress)}%`}
            label="Today"
            sublabel={`${currentSleep} of ${sleepGoal}`}
          />
          <div>
            <div className="text-lg font-semibold text-foreground" style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Today</div>
            <div className="text-sm text-muted-foreground">{currentSleep} of {sleepGoal}</div>
            <div className="text-sm font-medium mt-1" style={{ color: getStatusColor(progress) }}>{getStatusMessage()}</div>
          </div>
        </div>

        {/* Weekly Average */}
        <div className="text-center flex flex-col items-center gap-3">
          <GlowRing
            value={Math.round(weeklyProgress)}
            max={100}
            color={getRingColor(weeklyProgress)}
            size={140}
            unit="complete"
            displayValue={`${Math.round(weeklyProgress)}%`}
            label="Weekly Avg"
            sublabel={`${weeklyAverage} of ${sleepGoal}`}
          />
          <div>
            <div className="text-lg font-semibold text-foreground" style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Weekly Avg</div>
            <div className="text-sm text-muted-foreground">{weeklyAverage} of {sleepGoal}</div>
            <div className="text-sm font-medium mt-1" style={{ color: getStatusColor(weeklyProgress) }}>
              {weeklyProgress >= 90 ? 'Excellent consistency!' : 'Room for improvement'}
            </div>
          </div>
        </div>
      </div>

      {/* Recommendation Card */}
      <div className="rounded-lg p-4" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
        <div className="flex items-start space-x-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 mt-0.5" style={{ background: 'rgba(0,212,255,0.1)' }}>
            <Icon name="Lightbulb" size={16} style={{ color: '#00D4FF' }} />
          </div>
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">Sleep Recommendation</h4>
            <p className="text-sm text-muted-foreground">{getRecommendation()}</p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mt-6">
        <div className="text-center">
          <div className="text-lg font-bold font-metric" style={{ color: '#00D4FF' }}>{sleepGoal}</div>
          <div className="text-xs text-muted-foreground">Daily Goal</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold font-metric" style={{ color: '#AAFF00' }}>{currentSleep}</div>
          <div className="text-xs text-muted-foreground">Last Night</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold font-metric" style={{ color: '#FF6B00' }}>{weeklyAverage}</div>
          <div className="text-xs text-muted-foreground">Week Avg</div>
        </div>
      </div>
    </div>
  );
};

export default SleepGoalProgress;